import React, { useState } from "react";
import styled from "styled-components";
import GroupTypeSelectPopup from "src/components/GroupTypeSelectPopup";
import { usePopupLocation } from "src/components/PopupLocation";
import { useGroupLocation } from "src/hooks";
import AlertModal from "src/components/YearBooking/components/common/AlertModal";
import { createTeeshotAxiosInstance } from "src/http";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { recoilStates } from "src/recoil/recoilStates";

interface Step2Props {
  onNext: (data: {
    groupNameValue: string;
    memberCountValue: string;
    activeGroupTypeId: string;
    activeDistrictId: string;
  }) => void;
  onPrev: () => void;
  formData?: any; // Step1에서 받은 데이터
}

export default function Step2RequiredInfo({ onNext, onPrev, formData }: Step2Props) {
  const [groupNameValue, setGroupNameValue] = useState("");
  const [memberCountValue, setMemberCountValue] = useState("");
  const [selectedGroupType, setSelectedGroupType] = useState("");
  const [isGroupTypePopupOpen, setIsGroupTypePopupOpen] = useState(false);
  // 모달 상태 관리
  const [showGroupNameErrorModal, setShowGroupNameErrorModal] = useState(false);
  const [showMemberErrorModal, setShowMemberErrorModal] = useState(false);
  const [showGroupTypeErrorModal, setShowGroupTypeErrorModal] = useState(false);
  const [showGroupLocationErrorModal, setShowGroupLocationErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 서버에서 지역 정보 가져오기
  const groupLocations = useGroupLocation();
  const navigate = useNavigate();
  const [_, setGroup] = useRecoilState(recoilStates.group);

  const { PopupLocation, propsToInject, activeChildId, activeParentId } = usePopupLocation();

  const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGroupNameValue(e.target.value);
  };

  // 숫자를 천 단위 구분자로 포맷팅하는 함수
  const formatNumberWithCommas = (value: string) => {
    if (!value) return "";
    const numericValue = value.replace(/[^0-9]/g, "");
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // 천 단위 구분자가 포함된 문자열에서 숫자만 추출하는 함수
  const extractNumericValue = (value: string) => {
    return value.replace(/[^0-9]/g, "");
  };

  const handleMemberCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = extractNumericValue(value);
    setMemberCountValue(numericValue);
  };

  const handleNext = () => {
    if (!groupNameValue?.trim()) {
      setShowGroupNameErrorModal(true); // 상태 변경 → 컴포넌트 재렌더링 → 모달 표시
      return;
    }
    if (!memberCountValue?.trim()) {
      setShowMemberErrorModal(true); // 상태 변경 → 컴포넌트 재렌더링 → 모달 표시
      return;
    }
    if (!selectedGroupType?.trim()) {
      setShowGroupTypeErrorModal(true); // 상태 변경 → 컴포넌트 재렌더링 → 모달 표시
      return;
    }
    if (!activeParentId) {
      setShowGroupLocationErrorModal(true); // 상태 변경 → 컴포넌트 재렌더링 → 모달 표시
      return;
    }
    // YearGroupRegister와 동일한 activeDistrictId 생성 로직
    const activeDistrictId = (() => {
      if (!activeParentId) {
        return null;
      }
      const adjustedChildId = (() => {
        if (!activeChildId) {
          return "000";
        }
        if (activeChildId < 100) {
          return `0${activeChildId}`;
        }
        if (activeChildId < 10) {
          return `00${activeChildId}`;
        }
        return `${activeChildId}`;
      })();
      return `${activeParentId}${adjustedChildId}`;
    })();

    onNext({
      groupNameValue: groupNameValue.trim(),
      memberCountValue: String(memberCountValue || ""),
      activeGroupTypeId: selectedGroupType,
      activeDistrictId: activeDistrictId || "", // 활동지역 아이디
    });
  };

  // 필수 정보만으로 바로 등록
  const handleRegisterNow = async () => {
    if (isSubmitting) return;

    // 기존 검증 재사용
    if (!groupNameValue?.trim()) {
      setShowGroupNameErrorModal(true);
      return;
    }
    if (!memberCountValue?.trim()) {
      setShowMemberErrorModal(true);
      return;
    }
    if (!selectedGroupType?.trim()) {
      setShowGroupTypeErrorModal(true);
      return;
    }
    if (!activeParentId) {
      setShowGroupLocationErrorModal(true);
      return;
    }

    const activeDistrictId = (() => {
      if (!activeParentId) return null;
      const adjustedChildId = (() => {
        if (!activeChildId) return "000";
        if (activeChildId < 100) return `0${activeChildId}`;
        if (activeChildId < 10) return `00${activeChildId}`;
        return `${activeChildId}`;
      })();
      return `${activeParentId}${adjustedChildId}`;
    })();

    const submitData = {
      groupNameValue: groupNameValue.trim(),
      proposerNameValue: String(formData?.proposerNameValue ?? "").trim(),
      memberCountValue: String(memberCountValue || "").trim(),
      contactNumberValue: String(formData?.contactNumberValue ?? "").trim(),
      groupSnsUrlValue: "",
      groupIntroductionValue: "",
      activeGroupTypeId: selectedGroupType,
      activeDistrictId: activeDistrictId,
      groupImgBase64: undefined,
    } as const;

    try {
      setIsSubmitting(true);

      // 등록 API 호출
      const res = await createTeeshotAxiosInstance().post("/yearBooking/group", {
        submitData,
      });
      if (res.data.success === false) {
        throw new Error(res.data?.message || "단체 등록 실패");
      }

      // 그룹 정보 조회
      const groupResult = await createTeeshotAxiosInstance().get("/yearBooking/group");
      const groupResultData = groupResult?.data?.data;
      if (!groupResultData?.group || !groupResultData?.members) {
        setErrorMessage("그룹정보 조회오류");
        setShowErrorModal(true);
        return;
      }
      setGroup({ ...groupResultData.group, members: groupResultData.members });

      // 성공 모달
      setShowSuccessModal(true);
    } catch (error: any) {
      console.log("error", error);
      let errorMessage = "단체 등록 중 오류가 발생했습니다.";
      if (error?.code === "ECONNABORTED" || String(error?.message || "").includes("timeout")) {
        errorMessage = "서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요.";
      } else if (error?.response?.status >= 500) {
        errorMessage = "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      setErrorMessage(errorMessage);
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 단체 등록하기 버튼 활성화 조건: 모든 필수 항목이 입력/선택되어야 함
  const isRegisterButtonEnabled =
    groupNameValue?.trim() &&
    memberCountValue?.trim() &&
    selectedGroupType?.trim() &&
    activeParentId;

  const handleGroupTypeSelect = (groupTypeId: string) => {
    setSelectedGroupType(groupTypeId);
  };

  // ID를 이름으로 변환하는 함수
  const getGroupTypeName = (id: string) => {
    const groupTypeMap = {
      "1": "연습장 동호회",
      "2": "학교 동문회",
      "3": "지역 동호회",
      "4": "온라인 동호회(밴드,카페 등)",
      "5": "기업 동호회",
      "6": "골프 업종 모임",
      "7": "친목 모임",
      "8": "기타 모임",
    };
    return groupTypeMap[id as keyof typeof groupTypeMap] || "선택해주세요";
  };

  return (
    <StepContainer>
      {/* 성공 모달 */}
      {showSuccessModal && (
        <AlertModal
          onClose={() => setShowSuccessModal(false)}
          title="단체등록 완료"
          subtitle="단체 등록이 완료되었습니다."
          lines={["등록된 단체 정보로 연 단체 예약을 신청해보세요."]}
          confirmText="확인"
          onConfirm={() => {
            setShowSuccessModal(false);
            navigate("/year-booking/booking-register");
          }}
        />
      )}
      {/* 에러 모달 */}
      {showErrorModal && (
        <AlertModal
          onClose={() => setShowErrorModal(false)}
          title="등록 실패"
          subtitle={errorMessage}
          lines={["잠시 후 다시 시도해주세요."]}
          confirmText="확인"
          onConfirm={() => setShowErrorModal(false)}
        />
      )}
      {showGroupNameErrorModal && (
        <AlertModal
          onClose={() => setShowGroupNameErrorModal(false)}
          title="입력 오류"
          subtitle="단체명을 입력해주세요."
          lines={["단체명은 필수 입력 항목입니다."]}
          confirmText="확인"
          onConfirm={() => setShowGroupNameErrorModal(false)}
        />
      )}
      {/* 회원수 입력 오류 모달 */}
      {showMemberErrorModal && (
        <AlertModal
          onClose={() => setShowMemberErrorModal(false)}
          title="입력 오류"
          subtitle="회원 수를 입력해주세요."
          lines={["회원 수는 필수 입력 항목입니다."]}
          confirmText="확인"
          onConfirm={() => setShowMemberErrorModal(false)}
        />
      )}
      {/* 단체구분 입력 오류 모달 */}
      {showGroupTypeErrorModal && (
        <AlertModal
          onClose={() => setShowGroupTypeErrorModal(false)}
          title="입력 오류"
          subtitle="단체구분을 선택해주세요."
          lines={["단체구분은 필수 입력 항목입니다."]}
          confirmText="확인"
          onConfirm={() => setShowGroupTypeErrorModal(false)}
        />
      )}
      {/* 활동지역 입력 오류 모달 */}
      {showGroupLocationErrorModal && (
        <AlertModal
          onClose={() => setShowGroupLocationErrorModal(false)}
          title="입력 오류"
          subtitle="활동지역을 선택해주세요."
          lines={["활동지역은 필수 입력 항목입니다."]}
          confirmText="확인"
          onConfirm={() => setShowGroupLocationErrorModal(false)}
        />
      )}
      <MainContent>
        <PageTitle>단체 필수 정보를 입력해주세요.</PageTitle>
        <FormField>
          <FieldLabel>
            <RequiredAsterisk>*</RequiredAsterisk>
            <LabelText>단체명</LabelText>
          </FieldLabel>
          <InputField $active={Boolean(groupNameValue)}>
            <StyledInput
              id="group-name"
              value={groupNameValue}
              onChange={handleGroupNameChange}
              placeholder="단체명을 입력해주세요. (예시: 티샷 동호회)"
            />
          </InputField>
        </FormField>
        <FormField>
          <FieldLabel>
            <RequiredAsterisk>*</RequiredAsterisk>
            <LabelText>단체회원 수</LabelText>
          </FieldLabel>
          <InputField $active={Boolean(memberCountValue)}>
            <StyledInput
              id="member-count"
              type="text"
              value={formatNumberWithCommas(memberCountValue)}
              onChange={handleMemberCountChange}
              placeholder="단체회원의 수를 입력해주세요. (예시: 50)"
            />
          </InputField>
        </FormField>
        <FormField>
          <FieldLabel>
            <RequiredAsterisk>*</RequiredAsterisk>
            <LabelText>단체구분</LabelText>
          </FieldLabel>
          <InputField
            $active={Boolean(selectedGroupType)}
            onClick={() => setIsGroupTypePopupOpen(true)}
          >
            <InputText $isPlaceholder={!selectedGroupType}>
              {selectedGroupType
                ? getGroupTypeName(selectedGroupType)
                : "단체의 구분을 선택해주세요."}
            </InputText>
            <Chevron aria-hidden>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 10l5 5 5-5"
                  stroke="#79818a"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </Chevron>
          </InputField>
        </FormField>
        <FormField>
          <FieldLabel>
            <RequiredAsterisk>*</RequiredAsterisk>
            <LabelText>활동지역</LabelText>
          </FieldLabel>
          {groupLocations && (
            <PopupLocation
              {...propsToInject}
              inputId="group-location"
              items={groupLocations}
              // label="활동지역"
              isEssential={true}
              placeholder="활동하는 지역을 선택해주세요."
            />
          )}
        </FormField>
      </MainContent>
      <ButtonContainer>
        <PrevButton onClick={onPrev}>이전</PrevButton>
        <RegisterButton
          onClick={handleRegisterNow}
          disabled={isSubmitting || !isRegisterButtonEnabled}
        >
          {isSubmitting ? "등록 중..." : "단체 등록하기"}
        </RegisterButton>
      </ButtonContainer>
      <GroupTypeSelectPopup
        isOpen={isGroupTypePopupOpen}
        onClose={() => setIsGroupTypePopupOpen(false)}
        onSelect={handleGroupTypeSelect}
        selectedValue={selectedGroupType}
      />
    </StepContainer>
  );
}

const StepContainer = styled.div`
  margin-top: 28px;
  padding: 16px;
  width: 100%;
  height: calc(100dvh - 55px); /* 헤더 높이 제외 */
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;
`;

const MainContent = styled.div`
  flex: 1;
  padding-bottom: 120px; /* 하단 버튼 공간 확보 */
`;

const PageTitle = styled.h1`
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  height: 24px;
  margin: 0 0 32px 0;
`;

const FormField = styled.div`
  margin-bottom: 30px;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  height: 18px;
`;

const RequiredAsterisk = styled.span`
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #f97000;
`;

const LabelText = styled.span`
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #1a1a1a;
`;

const InputField = styled.div<{ $active?: boolean }>`
  display: flex;
  position: relative;
  width: 100%;
  height: 44px;
  padding: 13px 34px 13px 10px;
  align-items: center;
  border-radius: 6px;
  border: 1px solid ${(p) => (p.$active ? "#007bff" : "#d1d5d8")};
  background: #fdfdfd;
  cursor: pointer;
  &:focus-within {
    border-color: #007bff;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;

  &::placeholder {
    color: #79818a;
    font-size: 14px;
  }
`;

const InputText = styled.span<{ $isPlaceholder?: boolean }>`
  color: ${(p) => (p.$isPlaceholder ? "#79818a" : "#1a1a1a")};
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
`;

const Chevron = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  padding: 20px 16px;
  background: #fdfdfd;
  z-index: 0;
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 800px;
`;

const PrevButton = styled.button`
  flex: 1;
  height: 60px;
  padding: 13px 10px;
  border-radius: 10px;
  border: 1px solid #1a1a1a;
  background: #fdfdfd;
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

const RegisterButton = styled.button<{ disabled?: boolean }>`
  flex: 1;
  height: 60px;
  padding: 13px 10px;
  border-radius: 10px;
  border: none;
  background: ${(props) => (props.disabled ? "#d1d5d8" : "#004B49")};
  color: white;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};

  &:hover {
    background: ${(props) => (props.disabled ? "#d1d5d8" : "#003a37")};
  }
`;
